<template>
  <!-- The blocking notification the backend can push (norm 4: the one interruption allowed to take the
       whole screen, Appendix C, N4). On the design system's dialog: a dark sheet, who sends it, the
       title, the text and its options as the actions. Back, Escape, the X and, since 13-oct-2026, a tap
       outside close it (every dialog of the system closes on its scrim, as Adrián asked). -->
  <BzDialog
    card
    :visible="Boolean(shouldShow && notification)"
    band="blocking"
    :eyebrow="notification && notification.source ? `${$t('message.messageFrom')} ${notification.source}` : undefined"
    :title="notification ? notification.title : ''"
    :close-label="$t('message.close')"
    mask-testid="notification-overlay"
    title-testid="notification-title"
    close-testid="notification-close"
    @dismiss="handleClose"
  >
    <template v-if="notification">
      <div v-if="notification.image" class="notification-image-container">
        <img :src="notification.image" alt="" class="notification-image">
      </div>
      <p v-if="notification.body" class="notification-body" data-testid="notification-body">{{ notification.body }}</p>
      <div v-if="notification.timeout && totalTime > 0 && remainingSeconds > 0" :key="notification.id" class="timeout-container" data-testid="notification-timeout">
        <div class="timeout-progress-bar">
          <div class="timeout-progress" :style="{ animationDuration: `${totalTime}ms` }"></div>
        </div>
        <div class="timeout-text">
          <!-- DM Sans has no tabular figures: the number gets a slot as wide as its widest value, so the
               words after it stay still while it counts down, as a native timer's do. -->
          <span class="timeout-seconds" :style="{ minWidth: `${String(Math.ceil(totalTime / 1000)).length}ch` }">{{ remainingSeconds }}</span>
          {{ $t('message.secondsRemaining') }}
        </div>
      </div>
    </template>
    <template v-if="notification && notification.options && notification.options.length > 0" #footer>
      <!-- The options are the notification's actions, each a 72 px button (norm 9). The backend does not
           say which one is the main one: one option is the primary; two or more are secondary and weigh
           the same. -->
      <div class="notification-options" :class="{ 'notification-options--stack': notification.options.length > 2 }">
        <BzButton
          v-for="(option, index) in notification.options"
          :key="index"
          size="lg"
          :kind="notification.options.length === 1 ? 'primary' : 'secondary'"
          :label="option"
          :data-testid="`notification-option-${index}`"
          @click="handleOptionClick(getOptionId(index))"
        />
      </div>
    </template>
  </BzDialog>
  <audio ref="notificationSound" src="/notification.mp3" preload="auto"></audio>
</template>

<script>
import Logger from '@/logger';
import { reportAudioPlaybackOk, reportAudioPlaybackBlocked } from '@/audio';
import { restGet } from '@/restApiFallback';
import BzButton from '@/ui/components/BzButton.vue';
import BzDialog from '@/ui/components/BzDialog.vue';

const logger = new Logger({screen: 'NotificationComponent'});

export default {
  name: 'NotificationComponent',
  components: { BzButton, BzDialog },
  props: {
    notification: {
      type: Object,
      default: null
    },
    restaurantId: {
      type: String,
      default: ''
    }
  },
  emits: ['notification-closed', 'option-selected'],
  data() {
    return {
      shouldShow: false,
      remainingSeconds: 0,
      totalTime: 0,
      progressInterval: null,
      closeTimer: null
    };
  },
  watch: {
    notification: {
      immediate: true,
      handler(newNotification) {
        if (newNotification) {
          this.shouldShow = true;
          this.playNotificationSound();
          
          // Reset the countdown of a previous one.
          this.clearProgressInterval();
          this.totalTime = 0;
          this.remainingSeconds = 0;

          // Auto-hide the notification after the specified timeout (if provided). The bar empties
          // itself, as one CSS animation of `transform` (norm 1: nothing but the compositor moves while
          // the camera runs under it); only the seconds of the text are counted here, once a second.
          if (newNotification.timeout) {
            const timeout = parseInt(newNotification.timeout) * 1000;
            if (!isNaN(timeout) && timeout > 0) {
              this.totalTime = timeout;
              const endsAt = Date.now() + timeout;
              this.remainingSeconds = Math.ceil(timeout / 1000);
              this.progressInterval = setInterval(() => {
                this.remainingSeconds = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
                if (this.remainingSeconds <= 0) this.stopCountdown();
              }, 1000);

              this.closeTimer = setTimeout(() => {
                this.handleClose();
              }, timeout);
            }
          }
        }
      }
    }
  },
  beforeUnmount() {
    this.clearProgressInterval();
  },
  methods: {
    stopCountdown() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },

    // The countdown and the auto-close of this notification: a new one, or this one closing, ends both
    // (an old timer must not close the next notification).
    clearProgressInterval() {
      this.stopCountdown();
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    },
    
    handleClose() {
      this.shouldShow = false;
      this.clearProgressInterval();
      this.$emit('notification-closed', this.notification?.id);
    },
    
    getOptionId(index) {
      // Return the option_id if available, otherwise return the option text
      if (this.notification.option_ids && this.notification.option_ids[index] !== undefined) {
        return this.notification.option_ids[index] || 'none'; // Use 'none' for null values
      }
      return this.notification.options[index];
    },
    
    handleOptionClick(optionId) {
      const optionIndex = this.notification.option_ids ? 
        this.notification.option_ids.indexOf(optionId) : 
        -1;
      
      const optionText = optionIndex >= 0 ? 
        this.notification.options[optionIndex] : 
        optionId;
      
      logger.console(`handleOptionClick - Option clicked: ${optionText} (ID: ${optionId})`);
      
      // First close the notification immediately
      // Emit event to parent component with the option ID
      const notificationId = this.notification?.id;
      this.$emit('option-selected', { notificationId, option: optionId });
      
      // Mark notification as closed without emitting the closed event
      // This prevents double-processing in the parent component
      this.shouldShow = false;
      this.clearProgressInterval();
      
      // Send response to API in the background if endpoint is available
      if (this.notification?.endpoint) {
        // Use the endpoint directly from the notification data
        const path = `${this.notification.endpoint}?modal_id=${notificationId}&restaurant_id=${this.restaurantId}&response=${optionId}`;
        
        // Send the request asynchronously without awaiting
        this.sendOptionResponse(path);
      }
    },
    
    async sendOptionResponse(path) {
      try {
        logger.console(`sendOptionResponse - Sending request to: ${path}`);
        
        // Use the existing get method
        const result = await restGet({
          apiName: 'PRO-notification-API',
          path: path,
        });

        logger.console('sendOptionResponse - Response received');
        
        // Check if response was successful
        if (result) {
          logger.console('sendOptionResponse - Notification response sent successfully');
        } else {
          logger.error('sendOptionResponse - Notification response failed');
        }
      } catch (error) {
        logger.error('sendOptionResponse - Error sending notification response', {error: error});
      }
    },
    
    playSound(soundRef) {
      const sound = this.$refs[soundRef];
      if (sound) {
        sound.load(); // Ensure the audio is loaded
        // Whether the notification was actually audible goes to fx_audio_state (see
        // src/audio.js), reported only once the fallback below has had its turn: an element
        // whose play() lost a race but whose retry works is not a blocked tablet. The
        // reported error_name is the FIRST rejection's, the one that names the block
        // (NotAllowedError under the autoplay policy).
        sound.play().then(() => reportAudioPlaybackOk('notification')).catch(error => {
          logger.error(`Error playing ${soundRef} sound`, {error: error});
          // Fallback to a browser API sound if available
          if ('Audio' in window) {
            new Audio(sound.src).play().then(() => {
              reportAudioPlaybackOk('notification');
              logger.info(`Fallback audio playing ${soundRef} playback successful`);
            }).catch(fallbackError => {
              reportAudioPlaybackBlocked('notification', error);
              logger.error(`Fallback audio playback playing ${soundRef} failed`, {error: fallbackError});
            });
          } else {
            reportAudioPlaybackBlocked('notification', error);
          }
        });
      }
    },
    
    playNotificationSound() {
      this.playSound('notificationSound');
    }
  }
};
</script>

<style scoped>
/* The message at the rows' size with their line height: an alert's text, not a web page's paragraph. */
.notification-body {
  margin: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
  white-space: pre-line;
}

.notification-image-container {
  display: flex;
  justify-content: center;
  margin: 0 0 var(--bz-space-4);
}

.notification-image {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: var(--bz-radius-lg);
}

/* One or two options side by side, the same width; three or more, one under the other. */
.notification-options {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: var(--bz-target-gap);
  width: 100%;
}

.notification-options--stack {
  grid-auto-flow: row;
}

.timeout-container {
  margin-top: var(--bz-space-6);
}

/* A thin rounded track in the control grey, the time left in the track's active blue. No clip: the fill only
   ever shrinks inside the track, and a rounded clip over a scaling layer is a mask on every frame. */
.timeout-progress-bar {
  height: 4px;
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
}

/* The countdown empties from the right, in one animation the compositor runs. */
.timeout-progress {
  height: 100%;
  border-radius: var(--bz-radius-full);
  background: var(--bz-track-active);
  transform-origin: left center;
  animation-name: notification-countdown;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes notification-countdown {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

.timeout-seconds {
  display: inline-block;
  text-align: end;
}

.timeout-text {
  margin-top: var(--bz-space-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
  font-variant-numeric: tabular-nums;
}
</style>
